import { useState, useRef, useCallback, useEffect } from 'react'
import { toast } from 'sonner'
import { Plus, Trash2, Pencil } from 'lucide-react'
import { useBandContext } from '@/contexts/BandContext'
import { useSongs } from '@/hooks/useSongs'
import { searchSongs, fetchDeezerTrackYear } from '@/lib/songSearch'
import { formatDuration } from '@/lib/setlistGenerator'
import { StarRating } from '@/components/StarRating'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import type { SongSearchResult } from '@/types'

export const RepertoirePage = () => {
    const { bands, selectedBandId } = useBandContext()
    const { songs, addSong, updateSong, removeSong } = useSongs(selectedBandId || undefined)

    // Unified add song input
    const [songInput, setSongInput] = useState('')
    const [searchResults, setSearchResults] = useState<SongSearchResult[]>([])
    const [searching, setSearching] = useState(false)
    const [showDropdown, setShowDropdown] = useState(false)
    const [highlightedIndex, setHighlightedIndex] = useState(-1)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)
    const searchIdRef = useRef(0)

    // Song edit
    const [editingSongId, setEditingSongId] = useState<string | null>(null)
    const [editTitle, setEditTitle] = useState('')
    const [editArtist, setEditArtist] = useState('')
    const [editYear, setEditYear] = useState('')
    const [perfDuration, setPerfDuration] = useState('')
    const [editRating, setEditRating] = useState(0)

    // Filter
    const [minRating, setMinRating] = useState(0)

    // Debounced search as user types
    const debouncedSearch = useCallback((query: string) => {
        if (debounceRef.current) clearTimeout(debounceRef.current)
        const searchId = ++searchIdRef.current
        if (query.trim().length < 2) {
            setSearchResults([])
            setShowDropdown(false)
            return
        }
        debounceRef.current = setTimeout(async () => {
            setSearching(true)
            try {
                const results = await searchSongs(query.trim())
                // Ignore responses that arrive after a newer search was started
                if (searchId !== searchIdRef.current) return
                setSearchResults(results)
                setShowDropdown(results.length > 0)
                setHighlightedIndex(-1)
            } catch {
                // silently fail autocomplete
            } finally {
                if (searchId === searchIdRef.current) setSearching(false)
            }
        }, 400)
    }, [])

    useEffect(() => {
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
    }, [])

    // Close dropdown on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
                inputRef.current && !inputRef.current.contains(e.target as Node)) {
                setShowDropdown(false)
            }
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [])

    const handleInputChange = (value: string) => {
        setSongInput(value)
        debouncedSearch(value)
    }

    const handleSelectResult = async (result: SongSearchResult) => {
        setShowDropdown(false)
        setSongInput('')
        setSearchResults([])
        try {
            // Fetch year from track detail if not already present
            let year = result.year
            if (!year && result.source === 'deezer' && result.source_id) {
                year = await fetchDeezerTrackYear(result.source_id)
            }
            await addSong({
                title: result.title,
                artist: result.artist,
                is_original: false,
                duration_seconds: result.duration_seconds,
                performance_duration_seconds: result.duration_seconds,
                year,
                source: result.source,
                source_id: result.source_id,
            })
            toast.success(`Added "${result.title}"`)
        } catch {
            toast.error('Failed to add song')
        }
    }

    const handleAddOriginal = async () => {
        if (!songInput.trim() || !selectedBandId) return
        setShowDropdown(false)
        setSearchResults([])
        try {
            await addSong({
                title: songInput.trim(),
                artist: bands.find((b) => b.id === selectedBandId)?.name ?? '',
                is_original: true,
                duration_seconds: null,
                performance_duration_seconds: null,
                year: null,
                source: 'original',
                source_id: null,
            })
            setSongInput('')
            toast.success('Original song added')
        } catch {
            toast.error('Failed to add song')
        }
    }

    const handleInputKeyDown = (e: React.KeyboardEvent) => {
        if (showDropdown && searchResults.length > 0) {
            if (e.key === 'ArrowDown') {
                e.preventDefault()
                setHighlightedIndex((i) => Math.min(i + 1, searchResults.length - 1))
            } else if (e.key === 'ArrowUp') {
                e.preventDefault()
                setHighlightedIndex((i) => Math.max(i - 1, 0))
            } else if (e.key === 'Enter' && highlightedIndex >= 0) {
                e.preventDefault()
                handleSelectResult(searchResults[highlightedIndex])
                return
            }
        }
        if (e.key === 'Enter') {
            handleAddOriginal()
        }
    }

    const handleRatingChange = async (songId: string, rating: number) => {
        try {
            await updateSong(songId, { rating })
        } catch {
            toast.error('Failed to update rating')
        }
    }

    const handleSaveEdit = async () => {
        if (!editingSongId) return
        const parts = perfDuration.split(':')
        const secs = parts.length === 2
            ? parseInt(parts[0]) * 60 + parseInt(parts[1])
            : perfDuration ? parseInt(perfDuration) * 60 : null
        try {
            await updateSong(editingSongId, {
                title: editTitle.trim(),
                artist: editArtist.trim(),
                year: editYear ? parseInt(editYear, 10) : null,
                performance_duration_seconds: secs,
                rating: editRating,
            })
            setEditingSongId(null)
            setPerfDuration('')
            setEditRating(0)
            toast.success('Song updated')
        } catch {
            toast.error('Failed to update song')
        }
    }

    const filteredSongs = minRating > 0 ? songs.filter((s) => s.rating >= minRating) : songs

    return (
        <div className='mx-auto max-w-4xl p-6 space-y-6'>
            <h1 className='text-2xl font-bold'>Repertoire</h1>

            {selectedBandId && (
                <>
                    {/* Add song - unified input with autocomplete */}
                    <div className='relative'>
                        <div className='flex gap-2'>
                            <Input
                                ref={inputRef}
                                placeholder='Add new songs...'
                                value={songInput}
                                onChange={(e) => handleInputChange(e.target.value)}
                                onKeyDown={handleInputKeyDown}
                                onFocus={() => { if (searchResults.length > 0) setShowDropdown(true) }}
                            />
                            <Button size='icon' onClick={handleAddOriginal} disabled={!songInput.trim()}><Plus className='h-4 w-4' /></Button>
                        </div>
                        {searching && (
                            <div className='absolute right-28 top-2.5 text-xs text-muted-foreground'>Searching...</div>
                        )}
                        {showDropdown && searchResults.length > 0 && (
                            <div
                                ref={dropdownRef}
                                className='absolute z-50 mt-1 w-full max-h-72 overflow-y-auto rounded-md border bg-popover shadow-md'
                            >
                                {searchResults.map((r, i) => (
                                    <button
                                        key={`${r.source}-${r.source_id}-${i}`}
                                        type='button'
                                        className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-accent ${i === highlightedIndex ? 'bg-accent' : ''}`}
                                        onMouseEnter={() => setHighlightedIndex(i)}
                                        onClick={() => handleSelectResult(r)}
                                    >
                                        <div className='min-w-0 flex-1'>
                                            <span className='font-medium'>{r.title}</span>
                                            <span className='ml-2 text-muted-foreground'>{r.artist}</span>
                                            {r.year && <span className='ml-2 text-xs text-muted-foreground'>({r.year})</span>}
                                        </div>
                                        <div className='ml-2 flex shrink-0 items-center gap-2 text-xs text-muted-foreground'>
                                            {r.duration_seconds && <span>{formatDuration(r.duration_seconds)}</span>}
                                            <span className='capitalize'>[{r.source}]</span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Filter */}
                    <div className='flex items-center gap-4'>
                        <Label>Min rating:</Label>
                        <div className='flex items-center gap-2'>
                            <StarRating value={minRating} onChange={(v) => setMinRating(v === minRating ? 0 : v)} />
                            {minRating > 0 && (
                                <Button size='sm' variant='outline' onClick={() => setMinRating(0)}>Clear</Button>
                            )}
                        </div>
                    </div>

                    {/* Song list */}
                    <div className='space-y-1'>
                        {filteredSongs.map((song) => (
                            <div key={song.id} className='flex items-center gap-2 rounded border px-3 py-2 text-sm bg-background'>
                                <div className='min-w-0 flex-1'>
                                    <div className='truncate'>
                                        <span className='font-medium'>{song.title}</span>
                                        {song.is_original && <span className='ml-2 text-xs rounded bg-primary/10 px-1.5 py-0.5'>Original</span>}
                                    </div>
                                    <div className='truncate text-muted-foreground'>{song.artist}{song.year ? ` (${song.year})` : ''}</div>
                                </div>
                                <div className='ml-auto shrink-0 text-right'>
                                    <div className='text-xs text-muted-foreground tabular-nums font-mono'>
                                        {song.performance_duration_seconds ? formatDuration(song.performance_duration_seconds) : '—'}
                                    </div>
                                    <div className='text-xs text-muted-foreground'>
                                        Played {song.setlist_count}×
                                    </div>
                                </div>
                                <div className='hidden sm:block shrink-0'>
                                    <StarRating value={song.rating} onChange={(v) => handleRatingChange(song.id, v)} />
                                </div>
                                <Button
                                    variant='ghost'
                                    size='icon'
                                    className='h-7 w-7 shrink-0'
                                    onClick={() => {
                                        setEditingSongId(song.id)
                                        setEditTitle(song.title)
                                        setEditArtist(song.artist)
                                        setEditYear(song.year ? String(song.year) : '')
                                        setEditRating(song.rating)
                                        setPerfDuration(
                                            song.performance_duration_seconds
                                                ? `${Math.floor(song.performance_duration_seconds / 60)}:${String(song.performance_duration_seconds % 60).padStart(2, '0')}`
                                                : '',
                                        )
                                    }}
                                >
                                    <Pencil className='h-4 w-4' />
                                </Button>
                                <Button variant='ghost' size='icon' className='h-7 w-7 shrink-0 -ml-1' onClick={() => removeSong(song.id)}>
                                    <Trash2 className='h-4 w-4' />
                                </Button>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {/* Edit song dialog */}
            <Dialog open={!!editingSongId} onOpenChange={() => setEditingSongId(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Edit Song</DialogTitle>
                    </DialogHeader>
                    <div className='space-y-4'>
                        <div className='space-y-2'>
                            <Label>Title</Label>
                            <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} />
                        </div>
                        <div className='space-y-2'>
                            <Label>Artist</Label>
                            <Input value={editArtist} onChange={(e) => setEditArtist(e.target.value)} />
                        </div>
                        <div className='space-y-2'>
                            <Label>Year</Label>
                            <Input value={editYear} onChange={(e) => setEditYear(e.target.value)} placeholder='e.g. 1970' />
                        </div>
                        <div className='space-y-2'>
                            <Label>Performance Duration (m:ss)</Label>
                            <Input
                                placeholder='e.g. 4:30'
                                value={perfDuration}
                                onChange={(e) => setPerfDuration(e.target.value)}
                            />
                        </div>
                        <div className='space-y-2'>
                            <Label>Rating</Label>
                            <StarRating value={editRating} onChange={setEditRating} />
                        </div>
                    </div>
                    <Button onClick={handleSaveEdit}>Save</Button>
                </DialogContent>
            </Dialog>
        </div>
    )
}
